import { CustomerAddressModelType } from '@teinor/erp/types/company/customer/customerAddress'
import { SellOrderTypeShippingInversed } from '@teinor/erp/types/company/order/sell/sellOrder'
import { SellShippingModelType, SellShippingTypePriceInversed } from '@teinor/erp/types/company/order/sell/sellShipping'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { parseDictCustomerAddress, displayFullNameService, parseServicesFromDictShippingCompanies } from '../../../../../../app/erp/screens/Logistic/QuoteParent/SellParent/functions'
import { ButtonSecondary } from '../../../../../../baseComponents/Button'
import CustomError from '../../../../../../baseComponents/CustomError'
import { ModalMediumCentered } from '../../../../../../baseComponents/Modal'
import { ModalEditComponentProps } from '../../../../../../baseComponents/ModalsLayouts/types'
import { permissionCheck } from '../../../../../../baseComponents/PermissionChecker/function'
import ITHeaderText from '../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import { renderHeaderElements } from '../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftTabsProps, InputTableLeftColumnChange } from '../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { InputTableURLsTable, OrderHeaderURLs } from '../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { RowData } from '../../../../../../baseComponents/TwinTable/types'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import { DropDownSortFunct } from '../../../../../../forms/CustomSelect/DropDown'
import { emptyExtraLineFirstSortFunction } from '../../../../../../forms/CustomSelect/functions'
import FormRenderer from '../../../../../../forms/FormRenderer'
import { FormElement, FormRender } from '../../../../../../forms/FormRenderer/types'
import { parseAddressToOneLine } from '../../../../../../utils/globals/address'
import { twinFetchPostText, twinFetchPostJSON } from '../../../../../../utils/globals/data'
import { TwinDictionary, dictionaryComplexFromJsonArr } from '../../../../../../utils/globals/dictionary'
import { getCurrencySymbol, prepareVatTag } from '../../../../../../utils/globals/price'
import { returnClassOfSpanTemplate } from '../../../../../../utils/globals/tailwind'
import useOrderShippingTypePrice from '../../../../../../utils/hooks/orders/useOrderShippingTypePrice'
import useOrderTypeShippingDict from '../../../../../../utils/hooks/orders/useOrderTypeShippingDict'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { getUserPermissions } from '../../../../../../utils/reducers/getters'
import { SetCompleteDataTy } from '../../../OrderLayout/types'

type ModalConfigShippingSellOrderProps = InputTableLeftTabsProps & ModalEditComponentProps & { 
    orderURLs: InputTableURLsTable
    isReadOnly?: boolean
    orderHeaderURLs: OrderHeaderURLs
    modelId: string
}

const ModalConfigShippingSellOrder: React.FC<ModalConfigShippingSellOrderProps> = ({ isReadOnly, ...rest }) => {
    if (isReadOnly) {
        return <ModalConfigShippingSellOrderReadOnly {...rest} />
    }
    return (<ModalConfigShippingSellOrderEdit {...{ isReadOnly, ...rest }} />)
}

const ModalConfigShippingSellOrderReadOnly: React.FC<ModalConfigShippingSellOrderProps> = ({ headerData, setOpened, getCompleteData, setCompleteData, orderURLs, modelId, orderHeaderURLs }) => {
    const { t } = useTwinTranslation()
    const { dictTypePrice, memorizedAddress, dictTypeShipping, deciderHandleChange } = useModalConfigShippingSellOrderLogic({ headerData, setCompleteData, orderInputTableURLs: orderURLs, modelId, orderHeaderURLs, getCompleteData })
    const basicElements: FormElement<keyof SellShippingModelType | 'typeShipping'>[] = []
    const extraElements: FormElement<keyof SellShippingModelType>[] = []
    const configShipping = headerData?.OrderShippingId && !headerData.typeShipping
    
    if (headerData.typeShipping === SellOrderTypeShippingInversed['sendToCustomer'] && configShipping) {
        basicElements.push(
            {
                name: 'ShippingServiceId',
                component: 'Custom',
                render: () => <ITHeaderText label={t('shippingServices', 'Servicio de Transportista')} value={headerData?.OrderShipping?.ShippingService?.ShippingCompany?.name + ' - ' + headerData?.OrderShipping?.ShippingService?.name} className={returnClassOfSpanTemplate(2)} />,
            },
            {
                name: 'customerPays',
                component: 'Custom',
                render: ({ value }) => {
                    return (
                        String(value) === 'true' ?
                            <ITHeaderText label={t('postage', 'Portes')} value={t('postageDue', 'Portes debidos')} className={returnClassOfSpanTemplate(1)} />
                            : null
                    )
                },
            },
            {
                name: 'numberPackages',
                component: 'Custom',
                render: ({ value }) => <ITHeaderText label={t('numberPackages', 'Nº bultos')} value={value} className={returnClassOfSpanTemplate(1)} />,
            },
            {
                name: 'ShippingAddressRelationId',
                component: 'Custom',
                render: () => <ITHeaderText label={t('shippingAddress', 'Dirección de envio')} value={memorizedAddress} className={returnClassOfSpanTemplate(3)} />,
                extraProps: { memorizedAddress }
            },
        )
        if (!headerData?.OrderShipping?.customerPays) {
            extraElements.push(
                {
                    name: 'typePrice',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('shippingCalculationS', 'Cálculo trans.')} value={dictTypePrice[parseInt(String(value))]?.name} className={returnClassOfSpanTemplate(1)} />,
                }
            )
            if (headerData?.OrderShipping?.typePrice === SellShippingTypePriceInversed['introducePrice'] && !headerData?.hidePrices) {
                extraElements.push(
                    {
                        name: headerData.OrderShipping.priceVatIncluded ? 'finalPriceWithTax' : 'initialPriceWithoutTax',
                        component: 'Custom',
                        render: ({ value }) => <ITHeaderText label={t('priceToCustomer', 'Precio cliente')} value={value + getCurrencySymbol(headerData.CurrencyId) + ' - ' + prepareVatTag(headerData?.OrderShipping?.priceVatIncluded, t)} className={returnClassOfSpanTemplate(1)} />
                    }
                )
            }
        }
        extraElements.push(
            {
                name: 'weight',
                component: 'Custom',
                render: ({ value }) => <ITHeaderText label={t('weight', 'Peso')} value={(value === '0' || value === undefined ? '-' : value) + ' ' + t('kg', 'kg')} className={returnClassOfSpanTemplate(1)} />
            }
        )
    }
    
    const sections: FormRender<any> = [
        {
            cols: 3,
            className: 'modal_config_shipping_form text-start gap-x-30',
            elements: [
                {
                    name: 'typeShipping',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('typeShipping', 'Tipo de envío')} value={dictTypeShipping[parseInt(String(headerData?.typeShipping))]?.name} className={returnClassOfSpanTemplate(3)} />
                },
                ...basicElements,
                ...extraElements,
                
            ]
        }
    ]
    const parsedFields = renderHeaderElements(sections, deciderHandleChange, headerData?.OrderShipping)
    return (
        <ModalMediumCentered opened={true} setOpened={setOpened} onClickOut={true} className='modal_config_shipping modal_order_sell_layout gap-y-19 flex_height'>
            <h2 className='flex justify-start regular20'><TwinTrans transKey='infoShipping'>Información del transporte</TwinTrans></h2>
            <div className=' modal_negative_margin flex flex-auto flex-col'>
                <FormRenderer sections={parsedFields} className='flex-auto overflow-auto' />
            </div>
            <div className='flex justify-end gap-x-33 gap-y-20 buttons_modal_config_shipping'>
                <ButtonSecondary onClick={() => setOpened(null)} className='regular14 mt-10'>
                    <TwinTrans transKey='accept'>Aceptar</TwinTrans>
                </ButtonSecondary>
            </div>
        </ModalMediumCentered>
    )
}

interface ModalConfigShippingSellOrderLogicProps {
    headerData: RowData
    setCompleteData: SetCompleteDataTy
    orderInputTableURLs: InputTableURLsTable
    orderHeaderURLs: OrderHeaderURLs
    modelId: string
    getCompleteData: () => void
}

const useModalConfigShippingSellOrderLogic = ({ headerData, setCompleteData, orderInputTableURLs, modelId, orderHeaderURLs, getCompleteData }: ModalConfigShippingSellOrderLogicProps) => {
    const [dictCustomerAddress, setDictCustomerAddress] = useState<TwinDictionary | null>(null)
    const dictShippingServices = useMemo(() => dictionaryComplexFromJsonArr(headerData?.OrderShipping?.ShippingServices || [], 'ShippingServiceId'), [headerData?.OrderShipping?.ShippingServices])
    const memorizedAddress = useMemo(() => parseAddressToOneLine(headerData?.OrderShipping?.ShippingAddressRelation), [headerData?.OrderShipping?.ShippingAddressRelation])
    const dynamicFormData = useDynamicFormData(headerData, dictShippingServices)
    const dictTypePrice = useOrderShippingTypePrice()
    const dictTypeShipping = useOrderTypeShippingDict()
    const CustomerId = headerData?.CustomerId || ''

    const handleChange: InputTableLeftColumnChange = useCallback(async (id, field, value) => {
        setCompleteData((old) => {
            if (!old) {
                return null
            }
            old.header[field] = value
            return { ...old }
        })
        await twinFetchPostText(orderInputTableURLs?.updateHeaderData, { id, [field]: value })
    }, [orderInputTableURLs?.updateHeaderData, setCompleteData])

    const getAllCustomerAddress = useCallback(async () => {
        const items: CustomerAddressModelType[] | false = await twinFetchPostJSON('/api/app/customer/customerAddress/getAllCustomerAddresses', {
            where: { 'CustomerId': CustomerId }
        })
        parseDictCustomerAddress(items, setDictCustomerAddress)
    }, [setDictCustomerAddress, CustomerId])

    const handleChangeSellShipping: InputTableLeftColumnChange = useCallback(async (id: any, field: string, value: string | number) => {
        if ((field === 'initialPriceWithoutTax' || field === 'weight' || field === 'finalPriceWithTax') && value === '') {
            value = 0
        }
        setCompleteData((old: any) => {
            if (!old) {
                return null
            }
            const myData = JSON.parse(JSON.stringify(old))
            myData.header['OrderShipping'] = { ...myData.header['OrderShipping'], [field]: value }
            return { ...myData }
        })
        await twinFetchPostJSON(orderHeaderURLs.updateShipping, {
            id,
            [modelId]: headerData?.id,
            [field]: value,
            'WorkingCenterId': headerData?.WorkingCenterId
        }, {})

    }, [setCompleteData, headerData?.WorkingCenterId, headerData.id, orderHeaderURLs.updateShipping, modelId])

    const handleCreateSellShipping: InputTableLeftColumnChange = useCallback(async (id: any, field: string, value: any) => {
        const WorkingCenterId = field === 'ShippingAddressId' ? { 'WorkingCenterId': headerData?.WorkingCenterId } : undefined
        const result = await twinFetchPostJSON(orderHeaderURLs.createShipping, { [modelId]: headerData?.id, [field]: value, ...WorkingCenterId, CurrencyId: headerData.CurrencyId })
        if (result) {
            setCompleteData((old: any) => {
                if (!old) {
                    return null
                }
                const myData = JSON.parse(JSON.stringify(old))
                myData.header['OrderShipping'] = result
                myData.header['OrderShippingId'] = result.id
                return { ...myData }
            })
        }
    }, [setCompleteData, headerData, orderHeaderURLs.createShipping, modelId])

    const deciderHandleChange: InputTableLeftColumnChange = useCallback(async (id, field, value) => {
        if (field !== 'typeShipping') {
            if (headerData?.OrderShipping?.id) {
                await handleChangeSellShipping(headerData?.OrderShippingId, field, value)
            } else {
                await handleCreateSellShipping(headerData?.OrderShippingId, field, value)
            }
            if (field === 'ShippingAddressId' || field === 'ShippingServiceId') {
                getCompleteData()
            }
        } else {
            await handleChange(id, field, value)
            setCompleteData((old) => {
                if (!old) {
                    return null
                }
                const myData = JSON.parse(JSON.stringify(old))
                myData.header['OrderShipping'] = undefined
                myData.header['OrderShippingId'] = undefined
                return { ...myData }
            })
        }

    }, [handleChangeSellShipping, handleCreateSellShipping, headerData, setCompleteData, getCompleteData, handleChange])

    useEffect(() => {
        if (permissionCheck(getUserPermissions('customer.profile.address'), 'read')) {
            getAllCustomerAddress()
        }
    }, [getAllCustomerAddress])

    return { dictTypePrice, memorizedAddress, handleChange, dictTypeShipping, dictCustomerAddress, deciderHandleChange, dictShippingServices, dynamicFormData }
}

const ModalConfigShippingSellOrderEdit: React.FC<ModalConfigShippingSellOrderProps> = ({ headerData, setOpened, getCompleteData, setCompleteData, orderURLs, modelId, orderHeaderURLs }) => {
    const { t } = useTwinTranslation()
    const { dictTypePrice, dictTypeShipping, dictCustomerAddress, dictShippingServices, deciderHandleChange, dynamicFormData } = useModalConfigShippingSellOrderLogic({ headerData, setCompleteData, orderInputTableURLs: orderURLs, getCompleteData, modelId, orderHeaderURLs })
    const basicElements: FormElement<keyof SellShippingModelType | 'typeShipping'>[] = []
    const extraElements: FormElement<keyof SellShippingModelType>[] = []

    if (dynamicFormData.showAddress) {
        extraElements.push({
            name: 'ShippingAddressId',
            label: t('shippingAddress', 'Dirección de envio'),
            component: 'CustomSelectWithSearchBar',
            items: dictCustomerAddress || {},
            required: true,
            value: headerData?.OrderShipping?.ShippingAddressId || '',
            className: returnClassOfSpanTemplate(dynamicFormData.showAddress ? 2 : 3)
        })
    }
    if (dynamicFormData.showErrorShippingService) {
        extraElements.push({
            name: 'ShippingServiceId',
            component: 'Custom',
            render: () => <CustomError className={returnClassOfSpanTemplate(3)} textError={t('notShippingServicesAvailableForThisAddress', 'No hay servicios de transportista disponibles para esta dirección')} />
        })
    } else if (dynamicFormData.showInfo) {
        extraElements.push({
            name: 'ShippingServiceId',
            component: 'CustomSelectBase',
            items: dictShippingServices,
            label: t('shippingServices', 'Servicio de Transportista'),
            displayTextFunc: displayFullNameService,
            DropDownComponent: DropDownSortFunct,
            value: headerData?.OrderShipping?.ShippingServiceId,
            className: returnClassOfSpanTemplate(3),
            extraDropDownParams: {
                sortFunction: parseServicesFromDictShippingCompanies
            }
        },
            {
                name: 'customerPays',
                label: t('postageDue', 'Portes debidos'),
                component: 'CheckboxMargin',
                checked: headerData?.OrderShipping?.customerPays,
                className: returnClassOfSpanTemplate(1)
            },
            {
                name: 'numberPackages',
                label: t('numberPackages', 'Nº bultos'),
                component: 'InputNumberWithLabelMargin',
                value: headerData?.OrderShipping?.numberPackages,
                className: returnClassOfSpanTemplate(1) + ' mt-15',
            },
            {
                name: 'weight',
                component: 'InputNumberWithLabelMarginTextRight',
                label: t('weight', 'Peso'),
                textRight: t('kg', 'kg'),
                value: headerData?.OrderShipping.weight,
                classNameParent: returnClassOfSpanTemplate(1) + ' mt-15'
            })
        if (!dynamicFormData.customerPays) {
            extraElements.push({
                name: 'typePrice',
                label: t('shippingCalculationS', 'Cálculo trans.'),
                component: 'CustomSelect',
                items: dictTypePrice,
                value: headerData?.OrderShipping?.typePrice,
                className: returnClassOfSpanTemplate(3),
            })
        }
    }
    if (dynamicFormData.introducePrice) {
        extraElements.push(
            {
                name: headerData?.OrderShipping.priceVatIncluded ? 'finalPriceWithTax' : 'initialPriceWithoutTax',
                component: 'InputNumberWithLabelMarginTextRight',
                label: t('priceToCustomer', 'Precio cliente'),
                textRight: dynamicFormData.textRight,
                value: dynamicFormData.price,
                classNameParent: returnClassOfSpanTemplate(3) + ' mt-15 final_price_tax_form',
            }
        )
    }

    const sections: FormRender<any> = [
        {
            cols: 3,
            className: 'modal_config_shipping_form text-start gap-x-30',
            elements: [
                {
                    name: 'typeShipping',
                    label: t('typeShipping', 'Tipo de envío'),
                    component: 'CustomSelect',
                    items: dictTypeShipping,
                    required: true,
                    className: returnClassOfSpanTemplate(dynamicFormData.showAddress ? 1 : 3),
                    sortFunction: emptyExtraLineFirstSortFunction
                }
                ,
                ...basicElements,
                ...extraElements,

            ]
        }
    ]
    const parsedFields = renderHeaderElements(sections, deciderHandleChange, headerData)
    return (
        <ModalMediumCentered opened={true} setOpened={setOpened} onClickOut={true} className='modal_config_shipping modal_order_sell_layout gap-y-19 flex_height'>
            <h2 className='flex justify-start regular20'><TwinTrans transKey='configShipping'>Configurar transporte</TwinTrans></h2>
            <div className=' modal_negative_margin flex flex-auto flex-col'>
                <FormRenderer sections={parsedFields} className='flex-auto px-30 overflow-x-visible' />
            </div>
            <div className='flex justify-end gap-x-33 gap-y-20 buttons_modal_config_shipping'>
                <ButtonSecondary onClick={() => setOpened(null)} className='regular14 mt-10'>
                    <TwinTrans transKey='accept'>Aceptar</TwinTrans>
                </ButtonSecondary>
            </div>
        </ModalMediumCentered>
    )
}

const useDynamicFormData = (headerData: TwinDictionary, dictShippingServices: TwinDictionary) => {
    const { t } = useTwinTranslation()
    return {
        showInfo: headerData?.OrderShipping?.ShippingAddressId !== undefined && headerData?.OrderShipping?.ShippingAddressId && Object.keys(dictShippingServices)?.length && headerData?.typeShipping !== null,
        textRight: getCurrencySymbol(headerData.CurrencyId) + ' - ' + prepareVatTag(headerData?.OrderShipping?.priceVatIncluded, t),
        price: headerData?.OrderShipping?.priceVatIncluded ? headerData?.OrderShipping?.finalPriceWithTax : headerData?.OrderShipping?.initialPriceWithoutTax,
        showAddress: parseInt(headerData?.typeShipping) === SellOrderTypeShippingInversed['sendToCustomer'],
        showErrorShippingService: Object.keys(dictShippingServices)?.length && dictShippingServices[headerData?.OrderShipping?.ShippingServiceId]?.ShippingService['ShippingRate'] ? undefined : headerData?.OrderShipping?.id,
        introducePrice: parseInt(headerData?.OrderShipping?.typePrice) === SellShippingTypePriceInversed['introducePrice'],
        customerPays: headerData?.OrderShipping?.customerPays
    }
}

export default ModalConfigShippingSellOrder